import React, { useState, useEffect } from 'react';
import { Plus, Search, ShieldCheck, Star, Sparkles, Building, ArrowRight, CheckCircle2, ChevronRight, X, ExternalLink, Filter, Settings, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import SupplierCard from './SupplierCard';
import SupplierForm from './SupplierForm';
import ConfirmationModal from './ConfirmationModal';
import { loadSuppliers, createSupplier, updateSupplier, deleteSupplier, Supplier, SUPPLIER_CATEGORIES } from '../helpers/suppliersHelpers';

const SuppliersPage: React.FC = () => {
  const { isAdmin, isFornecedor, user } = useAuth();
  const { showToast } = useToast();

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);

  useEffect(() => {
    const fetchSuppliers = async () => {
      setLoadingSuppliers(true);
      const data = await loadSuppliers();
      setSuppliers(data);
      setLoadingSuppliers(false);
    };
    fetchSuppliers();
  }, []);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoriesFilter, setSelectedCategoriesFilter] = useState<string[]>([]);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);

  useEffect(() => {
    if (selectedSupplier) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedSupplier]);
  
  // Header banner configuration
  const [headerConfig, setHeaderConfig] = useState(() => {
    const saved = localStorage.getItem('masterclub_suppliers_header');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.titlePart1 !== undefined) return parsed;
      } catch (e) {
        // use default
      }
    }
    return {
      badge: 'Vitrine de Parceiros',
      titlePart1: 'Fornecedores',
      titleHighlight: 'Oficiais',
      titlePart2: '',
      description: 'Conecte-se diretamente com os fornecedores e integradores homologados pela Masterclub. Acesse tarifas exclusivas e impulsione suas vendas.'
    };
  });
  const [isHeaderModalOpen, setIsHeaderModalOpen] = useState(false);
  const [headerForm, setHeaderForm] = useState({ badge: '', titlePart1: '', titleHighlight: '', titlePart2: '', description: '' });

  // Modals state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState<Supplier | null>(null);

  const handleCreateOrUpdate = async (supplierData: Omit<Supplier, 'id'>) => {
    if (editingSupplier) {
      // Update
      const updated = await updateSupplier(editingSupplier.id, supplierData);
      if (updated) {
        setSuppliers(prev => prev.map((s) => s.id === editingSupplier.id ? updated : s));
        showToast({
          type: 'success',
          title: 'Fornecedor Atualizado!',
          message: 'As alterações foram salvas com sucesso.'
        });
        if (selectedSupplier && selectedSupplier.id === editingSupplier.id) {
          setSelectedSupplier(updated);
        }
      } else {
        showToast({
          type: 'error',
          title: 'Erro ao Atualizar',
          message: 'Não foi possível salvar as alterações.'
        });
      }
    } else {
      // Create
      const payload = {
        ...supplierData,
        user_id: isFornecedor ? user?.id : (supplierData.user_id || null)
      };
      const newSupplier = await createSupplier(payload);
      if (newSupplier) {
        setSuppliers(prev => [...prev, newSupplier]);
        showToast({
          type: 'success',
          title: 'Fornecedor Adicionado!',
          message: 'Novo parceiro oficial adicionado à vitrine.'
        });
      } else {
        showToast({
          type: 'error',
          title: 'Erro ao Adicionar',
          message: 'Não foi possível adicionar o fornecedor.'
        });
      }
    }
    setIsFormOpen(false);
    setEditingSupplier(null);
  };

  const handleDelete = async () => {
    if (supplierToDelete) {
      const success = await deleteSupplier(supplierToDelete.id);
      if (success) {
        setSuppliers(prev => prev.filter((s) => s.id !== supplierToDelete.id));
        showToast({
          type: 'success',
          title: 'Fornecedor Excluído!',
          message: 'O parceiro oficial foi removido.'
        });
        if (selectedSupplier && selectedSupplier.id === supplierToDelete.id) {
          setSelectedSupplier(null);
        }
      } else {
        showToast({
          type: 'error',
          title: 'Erro ao Excluir',
          message: 'Não foi possível remover o fornecedor.'
        });
      }
      setIsDeleteOpen(false);
      setSupplierToDelete(null);
    }
  };

  const handleCategoryFilterToggle = (category: string) => {
    if (selectedCategoriesFilter.includes(category)) {
      setSelectedCategoriesFilter(selectedCategoriesFilter.filter((c) => c !== category));
    } else {
      setSelectedCategoriesFilter([...selectedCategoriesFilter, category]);
    }
  };

  const filteredSuppliers = suppliers.filter((s) => {
    const matchesSearch =
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.descriptionShort.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategoriesFilter.length === 0 ||
      (s.categories && s.categories.some((cat) => selectedCategoriesFilter.includes(cat)));
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="flex-1 bg-[#121316] min-h-screen pt-24 pb-24 px-4 sm:px-6 md:px-8">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Banner / Header */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1a1d21] via-[#121316] to-[#0f1114] border border-gray-800 p-8 sm:p-12">
          {/* Subtle decoration background */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%230FFF55%22%20fill-opacity%3D%220.03%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-70" />
          <div className="absolute right-0 top-0 w-96 h-96 bg-[#0FFF55]/5 rounded-full filter blur-[80px] pointer-events-none" />

          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-2 bg-[#0FFF55]/10 border border-[#0FFF55]/20 px-3.5 py-1.5 rounded-full text-xs font-semibold text-[#0FFF55]">
                <Sparkles size={12} className="animate-pulse" />
                <span>{headerConfig.badge}</span>
                {isAdmin && (
                  <button
                    onClick={() => {
                      setHeaderForm({
                        badge: headerConfig.badge,
                        titlePart1: headerConfig.titlePart1 || '',
                        titleHighlight: headerConfig.titleHighlight || '',
                        titlePart2: headerConfig.titlePart2 || '',
                        description: headerConfig.description
                      });
                      setIsHeaderModalOpen(true);
                    }}
                    className="p-0.5 text-gray-500 hover:text-white rounded ml-1 transition-colors"
                    title="Editar cabeçalho"
                  >
                    <Settings size={12} />
                  </button>
                )}
              </div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                {headerConfig.titlePart1}{' '}
                {headerConfig.titleHighlight && (
                  <span className="text-[#0FFF55]">{headerConfig.titleHighlight}</span>
                )}{' '}
                {headerConfig.titlePart2}
              </h1>
              <p className="text-gray-400 text-sm sm:text-base max-w-2xl leading-relaxed">
                {headerConfig.description}
              </p>
            </div>

            {(isAdmin || isFornecedor) && (
              <button
                onClick={() => {
                  setEditingSupplier(null);
                  setIsFormOpen(true);
                }}
                className="flex-shrink-0 inline-flex items-center justify-center space-x-2 bg-[#0FFF55] hover:bg-[#0dd149] text-[#121316] font-bold px-6 py-3.5 rounded-2xl transition-all shadow-lg hover:shadow-[#0FFF55]/20"
              >
                <Plus size={18} />
                <span>Adicionar Fornecedor</span>
              </button>
            )}
          </div>
        </div>

        {/* Search Bar / Filter Panel */}
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:max-w-xl items-center relative">
            {/* Search Input */}
            <div className="relative w-full">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar fornecedores homologados..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-[#1a1d21]/60 hover:bg-[#1a1d21] border border-gray-800 focus:border-[#0FFF55]/60 rounded-xl text-white text-sm outline-none transition-all placeholder:text-gray-500"
              />
            </div>

            {/* Custom Dropdown categories select */}
            <div className="relative w-full sm:w-auto flex-shrink-0">
              <button
                type="button"
                onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                className={`w-full sm:w-[170px] px-4 py-3 bg-[#1a1d21]/60 hover:bg-[#1a1d21] border border-gray-800 hover:border-[#0FFF55]/40 rounded-xl text-white text-sm font-semibold flex items-center justify-between gap-2 transition-all ${
                  selectedCategoriesFilter.length > 0 ? 'border-[#0FFF55]/30 text-[#0FFF55]' : ''
                }`}
              >
                <Filter size={16} className={selectedCategoriesFilter.length > 0 ? 'text-[#0FFF55]' : 'text-gray-400'} />
                <span>
                  {selectedCategoriesFilter.length === 0
                    ? 'Categorias'
                    : `Categorias (${selectedCategoriesFilter.length})`}
                </span>
                <ChevronRight size={14} className={`transform transition-transform text-gray-400 ${isCategoryDropdownOpen ? 'rotate-90 text-[#0FFF55]' : ''}`} />
              </button>

              {/* Dropdown Box */}
              {isCategoryDropdownOpen && (
                <>
                  {/* Backdrop overlay to close when clicking outside */}
                  <div className="fixed inset-0 z-20" onClick={() => setIsCategoryDropdownOpen(false)} />
                  
                  <div className="absolute left-0 mt-2 w-72 bg-[#121316] border border-gray-800 rounded-2xl shadow-2xl p-4 z-30 space-y-3">
                    <div className="flex items-center justify-between pb-2 border-b border-gray-800">
                      <span className="text-xs font-bold text-white uppercase tracking-wider">Categorias</span>
                      {selectedCategoriesFilter.length > 0 && (
                        <button
                          onClick={() => setSelectedCategoriesFilter([])}
                          className="text-[10px] text-[#0FFF55] hover:underline font-bold"
                        >
                          Limpar tudo
                        </button>
                      )}
                    </div>

                    {/* Search inside dropdown */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-3 h-3" />
                      <input
                        type="text"
                        placeholder="Buscar categoria..."
                        value={categorySearchQuery}
                        onChange={(e) => setCategorySearchQuery(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 bg-[#1a1d21] border border-gray-800 focus:border-[#0FFF55]/50 rounded-xl text-white text-xs outline-none transition-all placeholder:text-gray-600"
                      />
                    </div>

                    {/* Categories Scroll Area */}
                    <div className="max-h-56 overflow-y-auto custom-scrollbar space-y-1 pt-1">
                      {SUPPLIER_CATEGORIES.filter((cat) =>
                        cat.toLowerCase().includes(categorySearchQuery.toLowerCase())
                      ).length === 0 ? (
                        <p className="text-[11px] text-gray-500 py-4 text-center">Nenhuma categoria encontrada</p>
                      ) : (
                        SUPPLIER_CATEGORIES.filter((cat) =>
                          cat.toLowerCase().includes(categorySearchQuery.toLowerCase())
                        ).map((cat) => {
                          const isSelected = selectedCategoriesFilter.includes(cat);
                          return (
                            <button
                              key={cat}
                              type="button"
                              onClick={() => handleCategoryFilterToggle(cat)}
                              className={`w-full flex items-center justify-between p-2 rounded-lg text-left text-xs transition-colors ${
                                isSelected
                                  ? 'bg-[#0FFF55]/10 text-[#0FFF55] font-semibold'
                                  : 'text-gray-400 hover:text-[#0FFF55] hover:bg-gray-800/40'
                              }`}
                            >
                              <span>{cat}</span>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                readOnly
                                className="rounded border-gray-800 text-[#0FFF55] focus:ring-0 focus:ring-offset-0 accent-[#0FFF55] pointer-events-none w-3.5 h-3.5"
                              />
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center text-xs text-gray-500 font-medium">
            Mostrando <span className="text-white mx-1">{filteredSuppliers.length}</span> de <span className="text-white mx-1">{suppliers.length}</span> fornecedores
          </div>
        </div>

        {/* Suppliers Grid */}
        {loadingSuppliers ? (
          <div className="text-center py-20 bg-[#1a1d21]/20 border border-gray-800/80 rounded-3xl">
            <div className="animate-spin w-10 h-10 border-4 border-[#0FFF55] border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-400 text-sm font-semibold">Carregando fornecedores parceiros...</p>
          </div>
        ) : filteredSuppliers.length === 0 ? (
          <div className="text-center py-20 bg-[#1a1d21]/20 border border-gray-800/80 rounded-3xl">
            <Building className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Nenhum fornecedor encontrado</h3>
            <p className="text-gray-400 text-sm max-w-md mx-auto">
              Experimente alterar os termos da busca ou filtre por outra categoria de produtos.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSuppliers.map((supplier) => {
              const canEdit = isAdmin || (isFornecedor && supplier.user_id === user?.id);
              const canDelete = isAdmin;
              return (
                <SupplierCard
                  key={supplier.id}
                  supplier={supplier}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  onEdit={(e) => {
                    e.stopPropagation();
                    setEditingSupplier(supplier);
                    setIsFormOpen(true);
                  }}
                  onDelete={(e) => {
                    e.stopPropagation();
                    setSupplierToDelete(supplier);
                    setIsDeleteOpen(true);
                  }}
                  onClick={() => setSelectedSupplier(supplier)}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Supplier Form Modal */}
      <SupplierForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingSupplier(null);
        }}
        onSave={handleCreateOrUpdate}
        initialSupplier={editingSupplier}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setSupplierToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Excluir Fornecedor"
        message={`Tem certeza que deseja excluir o fornecedor "${supplierToDelete?.name}"? Esta ação removerá permanentemente todos os seus dados da vitrine.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />

      {/* Detailed Modal View */}
      {selectedSupplier && (
        <div 
          className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-10 bg-black/90 backdrop-blur-sm overflow-y-auto"
          onClick={() => setSelectedSupplier(null)}
        >
          <div 
            className="relative w-full max-w-4xl bg-[#0f1114] border border-gray-800 rounded-3xl shadow-2xl overflow-hidden my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header Banner */}
            <div className="relative h-48 sm:h-64 bg-gradient-to-r from-gray-900 to-gray-800 overflow-hidden">
              {selectedSupplier.bannerUrl && (
                <img
                  src={selectedSupplier.bannerUrl}
                  alt={`${selectedSupplier.name} Banner`}
                  className="w-full h-full object-cover opacity-60"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f1114] to-transparent" />
              
              {/* Close Button overlay */}
              <button
                onClick={() => setSelectedSupplier(null)}
                className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/80 text-gray-300 hover:text-white rounded-full border border-gray-800 transition-colors z-20"
              >
                <X size={18} />
              </button>

              {/* Verified badge inside Header */}
              {selectedSupplier.isVerified && (
                <div className="absolute top-4 left-4 flex items-center space-x-1.5 bg-[#0FFF55]/10 border border-[#0FFF55]/30 px-3 py-1 rounded-full text-xs font-bold text-[#0FFF55]">
                  <ShieldCheck size={14} className="fill-[#0FFF55]/10" />
                  <span>PARCEIRO VERIFICADO</span>
                </div>
              )}
            </div>

            {/* Content Container */}
            <div className="p-6 sm:p-8 space-y-6">
              {/* Title & Logo Header row */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-gray-800/80">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#121316] border border-gray-800 rounded-2xl overflow-hidden p-1.5 flex-shrink-0">
                    <img
                      src={selectedSupplier.logoUrl || 'https://images.unsplash.com/photo-1572021335469-31706a17aaef?w=100&h=100&fit=crop'}
                      alt={selectedSupplier.name}
                      className="w-full h-full object-contain rounded-xl"
                      onError={(e) => {
                        e.currentTarget.src = 'https://images.unsplash.com/photo-1572021335469-31706a17aaef?w=100&h=100&fit=crop';
                      }}
                    />
                  </div>
                  <div>
                    <h2 className="text-2xl sm:text-3xl font-extrabold text-white">{selectedSupplier.name}</h2>
                    
                    {/* Rating & Categories inside detailed view */}
                    <div className="flex flex-wrap items-center gap-3 mt-1.5">
                      <div className="flex items-center space-x-1 text-yellow-500">
                        <Star className="w-4 h-4 fill-yellow-500" />
                        <span className="text-sm font-bold text-gray-200">{selectedSupplier.rating.toFixed(1)}</span>
                      </div>
                      <span className="text-xs text-gray-500">|</span>
                      <div className="flex flex-wrap gap-1">
                        {selectedSupplier.categories && selectedSupplier.categories.map((cat) => (
                          <span
                            key={cat}
                            className="px-2 py-0.5 bg-[#0FFF55]/10 border border-[#0FFF55]/20 text-[#0FFF55] rounded-full text-[10px] font-semibold"
                          >
                            {cat}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Edit Button for Admins or owner Suppliers */}
                {(isAdmin || (isFornecedor && selectedSupplier.user_id === user?.id)) && (
                  <button
                    onClick={() => {
                      setEditingSupplier(selectedSupplier);
                      setIsFormOpen(true);
                    }}
                    className="inline-flex items-center justify-center space-x-2 bg-gray-900 hover:bg-gray-800 text-gray-300 hover:text-white px-4 py-2 border border-gray-800 rounded-xl transition-all text-xs font-semibold"
                  >
                    <span>Editar Fornecedor</span>
                  </button>
                )}
              </div>

              {/* Description */}
              <div className="space-y-3">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Sobre a Parceria</h3>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed whitespace-pre-line">
                  {selectedSupplier.descriptionFull}
                </p>
              </div>

              {/* Informações adicionais */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center space-x-2.5 p-3.5 bg-[#121316] border border-gray-800/80 rounded-2xl">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400">
                    <Building size={16} />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Criação de Marca Própria</p>
                    <p className="text-sm font-bold text-white mt-0.5">
                      {selectedSupplier.has_private_label ? 'Sim, trabalha com marcas' : 'Não trabalha'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2.5 p-3.5 bg-[#121316] border border-gray-800/80 rounded-2xl">
                  <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center text-orange-400">
                    <Clock size={16} />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Tempo Médio de Entrega</p>
                    <p className="text-sm font-bold text-white mt-0.5">
                      {selectedSupplier.tempo_medio_entrega || 'Não informado'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Stats Mini Row */}
              <div className="grid grid-cols-2 gap-4 bg-[#121316] border border-gray-800/80 p-4 rounded-2xl">
                <div className="text-center py-2">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Envios Concluídos</p>
                  <p className="text-xl sm:text-2xl font-black text-[#0FFF55] mt-1">
                    {selectedSupplier.enviosCount.toLocaleString()}+
                  </p>
                </div>
                <div className="text-center py-2 border-l border-gray-800/80">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Produtos Ativos</p>
                  <p className="text-xl sm:text-2xl font-black text-white mt-1">
                    {selectedSupplier.produtosCount.toLocaleString()}+
                  </p>
                </div>
              </div>

              {/* How to host integration steps */}
              {selectedSupplier.hospedarPassos && selectedSupplier.hospedarPassos.trim() && (
                <div className="bg-[#121316] border border-[#0FFF55]/20 p-5 sm:p-6 rounded-2xl space-y-4">
                  <div className="flex items-center space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-[#0FFF55]" />
                    <h4 className="text-sm sm:text-base font-bold text-white">Como hospedar minha marca com este fornecedor?</h4>
                  </div>
                  <div className="text-gray-300 text-xs sm:text-sm leading-relaxed whitespace-pre-line pl-7 space-y-2">
                    {selectedSupplier.hospedarPassos.split('\n').filter(line => line.trim() !== '').map((line, idx) => (
                      <div key={idx} className="flex gap-2">
                        <span className="text-[#0FFF55] font-bold">{idx + 1}.</span>
                        <span>{line.replace(/^\d+\.\s*/, '')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-6 border-t border-gray-800 bg-[#121316]">
              <span className="text-xs text-gray-500">Parceria Homologada Masterclub Brasil</span>
              <div className="flex items-center space-x-3 w-full sm:w-auto">
                <button
                  onClick={() => setSelectedSupplier(null)}
                  className="w-full sm:w-auto px-5 py-2.5 bg-transparent hover:bg-gray-900 text-gray-400 hover:text-white text-xs font-semibold rounded-xl transition-colors"
                >
                  Voltar
                </button>
                {selectedSupplier.whatsapp && (
                  <a
                    href={selectedSupplier.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full sm:w-auto px-6 py-2.5 bg-[#25D366] hover:bg-[#128C7E] text-white text-xs font-bold rounded-xl flex items-center justify-center space-x-2 transition-colors"
                  >
                    <span>WhatsApp</span>
                  </a>
                )}
                <a
                  href={selectedSupplier.redirectUrl || 'https://mastershop.com.br'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto px-6 py-2.5 bg-[#0FFF55] hover:bg-[#0dd149] text-black text-xs font-bold rounded-xl flex items-center justify-center space-x-2 transition-colors"
                >
                  <span>Ir para Mastershop</span>
                  <ExternalLink size={14} />
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header Config Modal */}
      {isHeaderModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-md bg-[#1a1d23] border border-gray-800 rounded-2xl p-6 space-y-4">
            <h3 className="text-xl font-bold text-white">Editar Topo da Página</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-400">Etiqueta (Badge)</label>
                <input
                  type="text"
                  value={headerForm.badge}
                  onChange={(e) => setHeaderForm({ ...headerForm, badge: e.target.value })}
                  className="w-full mt-1 px-3 py-2 bg-[#0f1114] border border-gray-800 focus:border-[#0FFF55]/60 rounded-xl text-white text-sm outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-400">Título - Parte 1 (Texto Normal)</label>
                <input
                  type="text"
                  value={headerForm.titlePart1}
                  onChange={(e) => setHeaderForm({ ...headerForm, titlePart1: e.target.value })}
                  placeholder="Ex: Fornecedores"
                  className="w-full mt-1 px-3 py-2 bg-[#0f1114] border border-gray-800 focus:border-[#0FFF55]/60 rounded-xl text-white text-sm outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#0FFF55]">Título - Destaque (Texto em Verde)</label>
                <input
                  type="text"
                  value={headerForm.titleHighlight}
                  onChange={(e) => setHeaderForm({ ...headerForm, titleHighlight: e.target.value })}
                  placeholder="Ex: Oficiais"
                  className="w-full mt-1 px-3 py-2 bg-[#0f1114] border border-gray-800 focus:border-[#0FFF55]/60 rounded-xl text-white text-sm outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-400">Título - Parte 2 (Texto Normal)</label>
                <input
                  type="text"
                  value={headerForm.titlePart2}
                  onChange={(e) => setHeaderForm({ ...headerForm, titlePart2: e.target.value })}
                  placeholder="Ex: da nossa plataforma"
                  className="w-full mt-1 px-3 py-2 bg-[#0f1114] border border-gray-800 focus:border-[#0FFF55]/60 rounded-xl text-white text-sm outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-400">Descrição</label>
                <textarea
                  value={headerForm.description}
                  onChange={(e) => setHeaderForm({ ...headerForm, description: e.target.value })}
                  rows={3}
                  className="w-full mt-1 px-3 py-2 bg-[#0f1114] border border-gray-800 focus:border-[#0FFF55]/60 rounded-xl text-white text-sm outline-none resize-none"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setIsHeaderModalOpen(false)}
                className="px-4 py-2 bg-transparent text-gray-400 hover:text-white text-xs font-semibold rounded-xl"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  setHeaderConfig(headerForm);
                  localStorage.setItem('masterclub_suppliers_header', JSON.stringify(headerForm));
                  setIsHeaderModalOpen(false);
                  showToast({
                    type: 'success',
                    title: 'Cabeçalho Atualizado!',
                    message: 'As alterações do cabeçalho foram salvas.'
                  });
                }}
                className="px-5 py-2 bg-[#0FFF55] hover:bg-[#0dd149] text-black text-xs font-bold rounded-xl"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuppliersPage;
